import React from 'react';
import { Platform } from 'react-native';

import {
  capitalizeFirstLetter,
  ChangeGroup,
  getTaskAssignedRole,
} from '@components/ActivityList/activityHelpers';
import { ActivityItemDocuments } from '@components/ActivityList/ActivityItemDocuments';
import Box from '@components/Restyle/Box';

import ChatMessage from '../../components/Chat/ChatMessage';
import TagsCollection from '../../components/Chat/TagsCollection';
import {
  Audit,
  Attachment,
  Task,
  TaskUser,
  EntityUser,
  Message,
} from '../../graphql/generated';

export const getTaskActivityMessage = (
  event: TaskActivityType,
  messageBody?: string,
  changes?: ChangeGroup[],
  username?: string
) => {
  switch (event) {
    case TaskActivityType.TaskCompleted:
      return 'Completed this task';
    case TaskActivityType.TaskAssigned:
      return (
        'Added ' +
        (username ?? 'someone') +
        ' as Task ' +
        getTaskAssignedRole(changes)
      );
    case TaskActivityType.TaskCreated:
      return 'Created this task';
    case TaskActivityType.TaskCompletedRestore:
      return 'Restored this task';
    case TaskActivityType.TaskUnassigned:
      return 'Unassigned ' + (username ?? 'someone') + ' from this task';
    case TaskActivityType.TaskUnassignedSelf:
      return 'Left this task';
    case TaskActivityType.TaskReassigned: {
      const fromRole = capitalizeFirstLetter(changes?.[0]?.roles?.from || '');
      const toRole = capitalizeFirstLetter(changes?.[0]?.roles?.to || '');
      return `Changed ${
        username ?? '@(Removed User)'
      } role from ${fromRole} to ${toRole}`;
    }
    case TaskActivityType.MessageSent:
      return messageBody;
    case TaskActivityType.TaskDocument:
      return 'Added file to this task';
    default:
      return '';
  }
};

export enum TaskActivityType {
  TaskDocument = 'TaskDocument',
  MessageSent = 'MessageSent',
  TaskCreated = 'TaskCreated',
  TaskUpdated = 'TaskUpdated',
  TaskAssigned = 'TaskAssigned',
  TaskUnassigned = 'TaskUnassigned',
  TaskUnassignedSelf = 'TaskUnassignedSelf',
  TaskCompleted = 'TaskCompleted',
  TaskCompletedRestore = 'TaskCompletedRestore',
  TaskReassigned = 'TaskReassigned',
  Default = '',
}

const allowedActivities = [
  TaskActivityType.TaskCompleted,
  TaskActivityType.TaskAssigned,
  TaskActivityType.TaskCreated,
  TaskActivityType.TaskCompletedRestore,
  TaskActivityType.TaskUnassigned,
  TaskActivityType.TaskUnassignedSelf,
  TaskActivityType.TaskReassigned,
  TaskActivityType.MessageSent,
];

export const filteredTaskAudits = (audits: Audit[]) => {
  return audits.filter((audit) =>
    allowedActivities.includes(audit.event as TaskActivityType)
  );
};

type TaskActivityItemProps = {
  audit: Audit;
  taskId?: string;
  externalNavigate?: any;
  doNotUseGlobalSearch?: boolean;
};

const TaskActivityItem: React.FC<TaskActivityItemProps> = ({
  audit,
  taskId,
  externalNavigate,
  doNotUseGlobalSearch = false,
}) => {
  const { attachment, user } = audit;
  // borderless tag styling is a web-only visual treatment
  const isTaskAssigned = audit.event === 'TaskAssigned';
  const isTaskUnassigned = audit.event === 'TaskUnassigned';
  const isTaskUnassignedSelf = audit.event === 'TaskUnassignedSelf';
  const isTaskReassigned = audit.event === 'TaskReassigned';

  const isMessageType = (item: Attachment): item is Message =>
    item.__typename === 'Message';

  const isTaskType = (item: Attachment): item is Task =>
    item.__typename === 'Task';

  const isTaskUserType = (item: Attachment): item is TaskUser =>
    item.__typename === 'TaskUser';

  const isEntityType = (item: Attachment): item is EntityUser =>
    item.__typename === 'EntityUser';

  if (!attachment) {
    return null;
  }

  if (isMessageType(attachment)) {
    return (
      <Box
        marginBottom={Platform.OS === 'web' ? 'xxs' : 'none'}
        pb={Platform.OS === 'web' ? 'xxxs' : 'none'}>
        <ChatMessage
          doNotUseGlobalSearch={doNotUseGlobalSearch}
          externalNavigate={externalNavigate}
          message={attachment}
          isActivityItem
          activityActor={user}
          taskIdFromTaskActivityLog={taskId}
        />
      </Box>
    );
  }

  if ((audit.documents?.length ?? 0) > 0) {
    return (
      <ActivityItemDocuments
        isActivityItem
        isTask={true}
        documents={audit.documents}
        actor={user}
        taskId={taskId}
      />
    );
  }

  if (isTaskType(attachment) && !taskId) {
    return (
      <TagsCollection
        doNotUseGlobalSearch={doNotUseGlobalSearch}
        externalNavigate={externalNavigate}
        event={audit.event}
        showAuthor={false}
        borderless
        isRow
        isActivityItem
        tagsCollection={{
          tasks: [attachment],
          project: attachment.project,
          author: user,
        }}
      />
    );
  }

  if (isTaskUserType(attachment) && !taskId) {
    return (
      <TagsCollection
        doNotUseGlobalSearch={doNotUseGlobalSearch}
        externalNavigate={externalNavigate}
        showAuthor={false}
        borderless
        isRow
        isActivityItem
        tagsCollection={{
          tasks: [attachment.task],
          project: attachment.task.project,
          author: user,
        }}
      />
    );
  }

  if (
    isEntityType(attachment) &&
    (isTaskAssigned ||
      isTaskUnassigned ||
      isTaskUnassignedSelf ||
      isTaskReassigned) &&
    !taskId
  ) {
    return (
      <TagsCollection
        doNotUseGlobalSearch={doNotUseGlobalSearch}
        externalNavigate={externalNavigate}
        showAuthor={false}
        borderless
        isRow
        isActivityItem
        tagsCollection={{
          tasks: attachment.entityTask ? [attachment.entityTask] : [],
          project: attachment.entityProject || attachment.entityTask.project,
          author: user,
        }}
      />
    );
  }
  return null;
};

export default TaskActivityItem;
